import type { MinigameEngine } from '@wyn/content';
import { smithingSim } from './smithing.ts';
import { brewingSim } from './brewing.ts';
import { horsemanshipSim } from './horsemanship.ts';
import { lockpickingSim } from './lockpicking.ts';
import { archerySim } from './archery.ts';
import { illuminationSim } from './illumination.ts';
import { type JoustingMove, jousting } from './jousting.ts';
import { type HuntingMove, hunting } from './hunting.ts';
import { type SiegeMove, siege } from './siege.ts';
import { type CardsMove, cards } from './cards.ts';
import { type AlchemyMove, alchemy } from './alchemy.ts';
import { type HagglingMove, haggling } from './haggling.ts';
import { type DiceMove, dice } from './dice.ts';
import { fishingSim } from './fishing.ts';
import { type MiningMove, mining } from './mining.ts';
import { woodcuttingSim } from './woodcutting.ts';

export type { Ware } from './types.ts';
export type { TurnEngineImpl } from './types.ts';

/**
 * Every engine, by name. Real-time engines are simulations the client plays and the server replays;
 * turn-based engines keep their state on the server and take one move per request.
 */
export const REALTIME_SIMS = {
  woodcutting: woodcuttingSim,
  fishing: fishingSim,
  smithing: smithingSim,
  brewing: brewingSim,
  horsemanship: horsemanshipSim,
  lockpicking: lockpickingSim,
  archery: archerySim,
  illumination: illuminationSim,
} as const;

export const TURN_ENGINES = {
  mining,
  dice,
  jousting,
  hunting,
  siege,
  cards,
  alchemy,
  haggling,
} as const;

export type RealtimeEngine = keyof typeof REALTIME_SIMS;
export type TurnEngine = keyof typeof TURN_ENGINES;

/** Every engine in content must have exactly one implementation here. */
const _covered: Record<MinigameEngine, RealtimeEngine | TurnEngine> = Object.fromEntries(
  [...Object.keys(REALTIME_SIMS), ...Object.keys(TURN_ENGINES)].map((k) => [k, k]),
) as Record<MinigameEngine, RealtimeEngine | TurnEngine>;
type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _exact: Exact<MinigameEngine, RealtimeEngine | TurnEngine> = true;
void _covered;
void _exact;

export type MinigameMove = MiningMove | DiceMove | JoustingMove | HuntingMove | SiegeMove | CardsMove | AlchemyMove | HagglingMove;
